import React from 'react';
import { Metro, State } from 'clinics/clinics';
import { S34Dropdowns, MilanBackgroundColors } from '@milanlaser/mlcomponents';
import { type PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';

type BuilderState<T> = {
    device: string;
    deviceSize: string;
    isBrowser: boolean;
    isServer: boolean;
} & T;

type BuilderProps<T> = {
    builderState: {
        state: BuilderState<T>;
    };
};

type S34DropdownsSectionsProps = BuilderProps<{
    metro: Metro;
    state: State;
    model: string;
    nearbyMetros?: Array<{ href: string; label: string }>;
}> & {
    className?: string;
    desktopPaddingTop?: PaddingSize;
    desktopPaddingBottom?: PaddingSize;
    mobilePaddingTop?: PaddingSize;
    mobilePaddingBottom?: PaddingSize;
    backgroundColor?: MilanBackgroundColors;
};

export function S34DropdownsSection(props: S34DropdownsSectionsProps) {
    const {
        className,
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
        ...restProps
    } = props;

    const {
        builderState: { state },
    } = restProps;

    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

    const isMetroPage = state.model === 'metro-page';
    const isStatePage = state.model === 'state-page';

    const location = state.model === 'metro-page' ? state?.metro?.name : state?.state?.name;
    const locationHeader = `Locations in ${location}`;

    let locationItems: Array<{ href: string; label: string }> = [];

    if (isStatePage) {
        locationItems = state.state.metros.map((metro) => {
            const firstStoreWithUrlPath = metro.stores?.find((store) => store.urlPath);
            if (!firstStoreWithUrlPath || !firstStoreWithUrlPath.urlPath) {
                throw new Error(`No urlPath found for metro: ${metro.name}. All stores should have urlPath property.`);
            }

            const urlParts = firstStoreWithUrlPath.urlPath.split('/');
            const metroUrl = urlParts.slice(0, -1).join('/');

            if (!metroUrl) {
                throw new Error(
                    `Invalid urlPath format for metro: ${metro.name}. Expected format: /locations/state/metro/store`
                );
            }

            return {
                href: metroUrl,
                label: metro.name,
            };
        });
    } else if (isMetroPage) {
        locationItems = state.metro.stores.map((store) => ({
            href: store.urlPath,
            label: store.marketingCity,
        }));
    }

    let nearbyMetroSection: { title: string; items: Array<{ href: string; label: string }> } | undefined;

    if (isMetroPage && state.nearbyMetros && state.nearbyMetros.length > 0) {
        nearbyMetroSection = { title: 'Other Nearby Metros', items: state.nearbyMetros };
    }

    return (
        <section className={`${topPaddingClasses} ${bottomPaddingClasses} ${className}`}>
            <div className="container">
                <S34Dropdowns
                    {...restProps}
                    title="Learn More About Our Services and Clinics"
                    locationHeader={locationHeader}
                    locationItems={locationItems}
                    nearbyMetrosSection={nearbyMetroSection}
                    areas={data.areaSections}
                    conditions={data.conditionSections}
                    resources={data.resourceSections}
                />
            </div>
        </section>
    );
}

export const data = {
    areaSections: [
        {
            title: 'Face',
            items: [
                { label: 'Face', href: '/areas/face' },
                { label: 'Eyebrows', href: '/areas/face/eyebrows' },
                { label: 'Upper Lip', href: '/areas/face/upper-lip' },
                { label: 'Beard', href: '/areas/beard' },
                { label: 'Chin', href: '/areas/face/chin' },
                { label: 'Neck', href: '/areas/face/neck' },
            ],
        },
        {
            title: 'Body',
            items: [
                { label: 'Arms', href: '/areas/arms' },
                { label: 'Legs', href: '/areas/legs' },
                { label: 'Stomach', href: '/areas/stomach' },
                { label: 'Back', href: '/areas/back' },
                { label: 'Buttocks', href: '/areas/buttocks' },
                { label: 'Hands', href: '/areas/hands' },
                { label: 'Underarm', href: '/areas/underarm' },
            ],
        },
        {
            title: 'Sensitive Areas',
            items: [
                { label: 'Bikini', href: '/areas/bikini' },
                { label: 'Brazilian', href: '/areas/brazilian' },
            ],
        },
        {
            title: 'Full Coverage',
            items: [{ label: 'Full Body', href: '/areas/full-body' }],
        },
    ],
    conditionSections: [
        {
            title: 'Conditions We Treat',
            items: [
                { label: 'PCOS', href: '/conditions/pcos' },
                { label: 'Folliculitis', href: '/conditions/folliculitis' },
                { label: 'Pilonidal Cysts', href: '/conditions/pilonidal-cysts' },
                { label: 'HS', href: '/conditions/hs' },
            ],
        },
        {
            title: 'Inclusive Clientele',
            items: [
                { label: 'For Men', href: '/inclusive-clientele/for-men' },
                { label: 'Dark Skin', href: '/inclusive-clientele/dark-skin' },
                { label: 'Amputees', href: '/inclusive-clientele/amputees' },
                { label: 'Bodybuilding', href: '/inclusive-clientele/bodybuilding' },
                { label: 'Transgender', href: '/inclusive-clientele/trans' },
            ],
        },
    ],
    resourceSections: [
        {
            title: 'Our Services',
            items: [
                {
                    label: 'How to Prepare for Laser Hair Removal',
                    href: '/how-to-prepare-for-laser-hair-removal',
                },
                {
                    label: 'What Is Laser Hair Removal',
                    href: '/what-is-laser-hair-removal',
                },
                {
                    label: 'Laser Hair Removal Aftercare',
                    href: '/laser-hair-removal-aftercare',
                },
            ],
        },
        {
            title: 'Must-Read Blog Posts',
            items: [
                {
                    label: 'Laser Hair Removal for Strawberry Legs',
                    href: '/blog/laser-hair-removal-for-strawberry-legs',
                },
                {
                    label: 'Laser Hair Removal & Tanning: Safe or Not?',
                    href: '/blog/laser-hair-removal-and-tanning',
                },
                {
                    label: "What Happens If You Don't Shave Before Laser?",
                    href: '/blog/what-happens-if-you-dont-shave-before-laser-hair-removal',
                },
                {
                    label: 'Sun Exposure After Laser Hair Removal',
                    href: '/blog/sun-exposure-after-laser-hair-removal',
                },
                {
                    label: 'Laser Hair Removal for Pilonidal Cysts',
                    href: '/blog/laser-hair-removal-for-pilonidal-cysts',
                },
                {
                    label: 'Laser Hair Removal vs Waxing: One Clear Winner',
                    href: '/blog/laser-hair-removal-vs-waxing',
                },
                {
                    label: 'Laser Hair Removal for Men',
                    href: '/blog/laser-hair-removal-for-men',
                },
            ],
        },
    ],
};
